import React from 'react';
import { motion } from 'framer-motion';
import Navbar from "../components/Navbar";
import {
    TrophyIcon,
    UserGroupIcon,
    ShieldCheckIcon,
    SparklesIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';

const About = () => {

  const coreValues = [
    {
      icon: <ShieldCheckIcon className="w-12 h-12" />,
      title: "Quality",
      description: "We never compromise on the quality of our materials and workmanship. Every project undergoes rigorous quality checks to ensure the highest standards.",
      gradientClass: "from-gray-500 to-gray-700",
    },
    {
      icon: <SparklesIcon className="w-12 h-12" />,
      title: "Innovation",
      description: "We constantly seek new and better ways to build sports infrastructure. Our team stays updated with the latest technologies and best practices.",
      gradientClass: "from-orange-500 to-orange-700",
    },
    {
      icon: <TrophyIcon className="w-12 h-12" />,
      title: "Excellence",
      description: "We strive for excellence in every project we undertake. Our commitment to delivering the best has earned us the trust of clients nationwide.",
      gradientClass: "from-gray-600 to-gray-800",
    },
    {
      icon: <UserGroupIcon className="w-12 h-12" />,
      title: "Integrity",
      description: "We conduct our business with honesty, transparency, and ethical practices. Our relationships with clients and partners are built on trust.",
      gradientClass: "from-orange-600 to-orange-800",
    },
  ];

  const certifications = [
    {
      title: "ISO 9001:2015",
      description: "Quality Management System Certification",
      icon: <ShieldCheckIcon className="w-8 h-8" />
    },
    {
      title: "FIFA Certified",
      description: "Approved for football turf installation",
      icon: <TrophyIcon className="w-8 h-8" />
    },
    {
      title: "IAAF Certified",
      description: "Approved for athletic track installation",
      icon: <ChartBarIcon className="w-8 h-8" />
    }
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">

        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-gray-800 to-orange-700 text-white py-16 md:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 text-center px-4">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 md:mb-6"
            >
              About Sports Experts Infra
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-base md:text-lg lg:text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed"
            >
              India's leading sports infrastructure company, transforming the sports landscape with world-class facilities and innovative solutions.
            </motion.p>
          </div>
        </section>

        {/* Achievements Section */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-12 md:mb-16"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 md:mb-6">Our Achievements</h2>
              <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
                Milestones that define our journey of excellence in sports infrastructure
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                { number: "500+", label: "Projects Completed" },
                { number: "50+", label: "Cities Covered" },
                { number: "10+", label: "Years Experience" },
                { number: "300+", label: "Happy Clients" }
              ].map((achievement, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-gray-100 text-center"
                >
                  <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-orange-600 mb-2 md:mb-3">
                    {achievement.number}
                  </div>
                  <div className="text-sm md:text-base lg:text-lg text-gray-700 font-medium">
                    {achievement.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-16">Mission & Vision</h2>
              <div className="grid md:grid-cols-2 gap-12">

                <div className="bg-gradient-to-br from-gray-700 to-gray-800 text-white p-8 rounded-xl shadow-lg hover:scale-105 transition-transform duration-300">
                  <div className="text-5xl mb-4">🎯</div>
                  <h3 className="text-2xl font-semibold mb-3">Our Mission</h3>
                  <p className="text-white/90 text-lg leading-relaxed">
                    To transform sports infrastructure in India by providing innovative, sustainable, and high-quality solutions that enable athletes to perform at their best.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-orange-600 to-orange-700 text-white p-8 rounded-xl shadow-lg hover:scale-105 transition-transform duration-300">
                  <div className="text-5xl mb-4">👁️</div>
                  <h3 className="text-2xl font-semibold mb-3">Our Vision</h3>
                  <p className="text-white/90 text-lg leading-relaxed">
                    To be the most trusted name in sports infrastructure development, known for excellence, innovation, and commitment to quality.
                  </p>
                </div>

              </div>
            </motion.div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-16">Our Core Values</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {coreValues.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={`text-center p-8 rounded-xl shadow-lg bg-gradient-to-br ${value.gradientClass} text-white hover:scale-105 transition duration-300`}
                  >
                    <div className="flex justify-center mb-4">{value.icon}</div>
                    <h3 className="text-2xl font-semibold mb-3">{value.title}</h3>
                    <p className="text-base opacity-90 leading-relaxed">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Certifications Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-16">Our Certifications</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {certifications.map((cert, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
                  >
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="text-orange-600">{cert.icon}</div>
                      <h3 className="text-xl font-semibold text-gray-800">{cert.title}</h3>
                    </div>
                    <p className="text-gray-600">{cert.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default About;
